import { useEffect, useRef, useState } from 'react';
import { DT } from '../../theme/designTokens';

// ─── Internal queue item ──────────────────────────────────────────────────────

interface ToastItem {
  id:      number;
  message: string;
}

let _nextId = 1;
type Listener = (items: ToastItem[]) => void;
const listeners = new Set<Listener>();
let _queue: ToastItem[] = [];

function notify(items: ToastItem[]) {
  _queue = items;
  listeners.forEach(l => l(items));
}

/** Call this from anywhere inside the React tree to show a social toast. */
export function showSocialToast(message: string) {
  const item: ToastItem = { id: _nextId++, message };
  notify([..._queue, item]);
}

// ─── Manager component ────────────────────────────────────────────────────────

/**
 * Renders one social toast at a time (FIFO queue).
 * Mount once in AppShell. Drive via showSocialToast().
 */
export function SocialToastManager() {
  const [queue,   setQueue]   = useState<ToastItem[]>([]);
  const [visible, setVisible] = useState<ToastItem | null>(null);
  const [entered, setEntered] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const listener: Listener = items => setQueue([...items]);
    listeners.add(listener);
    // Drain any items that were enqueued while this component was unmounted.
    if (_queue.length > 0) setQueue([..._queue]);
    return () => { listeners.delete(listener); };
  }, []);

  // Pop next item when slot is free.
  useEffect(() => {
    if (visible !== null || queue.length === 0) return;
    const [next, ...rest] = queue;
    notify(rest);
    setVisible(next);
    setEntered(false);
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, [visible, queue]);

  // Auto-dismiss after 4 s.
  useEffect(() => {
    if (!visible) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(dismiss, 4000);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  function dismiss() {
    setEntered(false);
    if (timerRef.current) clearTimeout(timerRef.current);
    // Brief delay so exit animation can play (CSS transition 200 ms).
    setTimeout(() => setVisible(null), 200);
  }

  if (!visible) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position:        'fixed',
        // Sits above GameInvitationToast (z:40) but below modals
        bottom:          'max(24px, env(safe-area-inset-bottom, 0px) + 16px)',
        left:            '50%',
        transform:       `translateX(-50%) translateY(${entered ? '0%' : '120%'})`,
        transition:      `transform 200ms ${DT.motion.easing}`,
        zIndex:          39,
        maxWidth:        320,
        width:           'calc(100vw - 48px)',
        backgroundColor: DT.color.cardBgElevated,
        border:          `1px solid ${DT.color.border}`,
        borderRadius:    DT.radius.md,
        padding:         '11px 16px',
        boxShadow:       '0 4px 16px rgba(0,0,0,0.4)',
        textAlign:       'center',
      }}
    >
      <p style={{
        margin:     0,
        fontSize:   13,
        fontFamily: DT.font.sans,
        color:      DT.color.textSecondary,
        lineHeight: 1.4,
      }}>
        {visible.message}
      </p>
    </div>
  );
}
